"use client";

import { Separator } from "@radix-ui/react-separator";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import cn from "classnames";
import { motion } from "framer-motion";
import {
	type CSSProperties,
	type ComponentProps,
	type ElementRef,
	type ReactNode,
	createContext,
	forwardRef,
	isValidElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

import { SidePanelOpenIcon } from "@moment/icons";

import { IconButton, type IconButtonProps } from "../Button";
import { InputText as Input } from "../InputText";
import { MomentTooltip } from "../MomentTooltip";
import { Sheet, SheetContent } from "../Sheet";
import { Skeleton } from "../Skeleton";
import { useTheme } from "../ThemeProvider";
import { useIsMobile } from "../hooks";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContext = {
	value?: string;
	values: string[];
	state: "expanded" | "collapsed";
	open: boolean;
	setOpen: (open: boolean) => void;
	openMobile: boolean;
	setOpenMobile: (open: boolean) => void;
	isMobile: boolean;
	toggleSidebar: () => void;
};

const SidebarContext = createContext<SidebarContext | null>(null);

function useSidebar() {
	const context = useContext(SidebarContext);
	if (!context) {
		throw new Error("useSidebar must be used within a SidebarProvider.");
	}

	return context;
}

const SidebarProvider = forwardRef<
	HTMLDivElement,
	ComponentProps<"div"> & {
		width?: string;
		value?: string;
		defaultOpen?: boolean;
		open?: boolean;
		onOpenChange?: (open: boolean) => void;
	}
>(
	(
		{
			width,
			value,
			defaultOpen = true,
			open: openProp,
			onOpenChange: setOpenProp,
			className,
			style,
			children,
			...props
		},
		ref
	) => {
		const isMobile = useIsMobile();
		const [openMobile, setOpenMobile] = useState(false);

		const values = useMemo(() => {
			let values: string[] = [];
			if (Array.isArray(children)) {
				values = children?.flatMap((child) => {
					if (!child || typeof child !== "object" || !("props" in child)) {
						return [];
					}
					return [child.props.value];
				});
			}
			return values;
		}, [children]);

		// This is the internal state of the sidebar.
		// We use openProp and setOpenProp for control from outside the component.
		const [_open, _setOpen] = useState(defaultOpen);
		const open = openProp ?? _open;
		const setOpen = useCallback(
			(value: boolean | ((value: boolean) => boolean)) => {
				if (setOpenProp) {
					return setOpenProp?.(typeof value === "function" ? value(open) : value);
				}

				_setOpen(value);

				// This sets the cookie to keep the sidebar state.
				document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
			},
			[setOpenProp, open]
		);

		// Helper to toggle the sidebar.
		const toggleSidebar = useCallback(() => {
			return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
		}, [isMobile, setOpen, setOpenMobile]);

		// Adds a keyboard shortcut to toggle the sidebar.
		useEffect(() => {
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
					event.preventDefault();
					toggleSidebar();
				}
			};

			window.addEventListener("keydown", handleKeyDown);
			return () => window.removeEventListener("keydown", handleKeyDown);
		}, [toggleSidebar]);

		// We add a state so that we can do data-state="expanded" or "collapsed".
		// This makes it easier to style the sidebar with Tailwind classes.
		const state = open ? "expanded" : "collapsed";

		const contextValue = useMemo<SidebarContext>(
			() => ({
				value,
				values,
				state,
				open,
				setOpen,
				isMobile,
				openMobile,
				setOpenMobile,
				toggleSidebar,
			}),
			[
				value,
				values,
				state,
				open,
				setOpen,
				isMobile,
				openMobile,
				setOpenMobile,
				toggleSidebar,
			]
		);

		let sidebars: ReactNode = [];
		if (Array.isArray(children)) {
			sidebars = children.filter((child) => isValidElement(child) && child.type === Sidebar);
		} else if (children && isValidElement(children) && children.type === Sidebar) {
			sidebars = [children];
		}

		let notSidebars: ReactNode = [];
		if (Array.isArray(children)) {
			notSidebars = children.filter(
				(child) => !isValidElement(child) || child.type !== Sidebar
			);
		} else if (children && (!isValidElement(children) || children.type !== Sidebar)) {
			notSidebars = [children];
		}

		return (
			<SidebarContext.Provider value={contextValue}>
				<MomentTooltip.Provider delayDuration={0}>
					<div
						style={
							{
								"--sidebar-width": width ?? SIDEBAR_WIDTH,
								"--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
								...style,
							} as CSSProperties
						}
						className={cn(
							"group/sidebar-wrapper flex min-h-full w-full has-[[data-variant=inset]]:bg-sidebar",
							className
						)}
						ref={ref}
						{...props}
					>
						{sidebars}
						{notSidebars}
					</div>
				</MomentTooltip.Provider>
			</SidebarContext.Provider>
		);
	}
);
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = forwardRef<
	HTMLDivElement,
	ComponentProps<"div"> & {
		value?: string;
		side?: "left" | "right";
		variant?: "sidebar" | "floating" | "inset";
		collapsible?: "offcanvas" | "icon" | "none";
		insetY?: boolean;
		insetX?: boolean;
		published?: boolean;
	}
>(
	(
		{
			value: thisValue,
			side = "left",
			variant = "sidebar",
			collapsible = "offcanvas",
			className,
			insetY = true,
			insetX = true,
			children,
			published = true,
			...props
		},
		ref
	) => {
		const { value, values, isMobile, state, openMobile, setOpenMobile } = useSidebar();

		if (collapsible === "none") {
			return (
				<div
					className={cn(
						"flex h-full w-[--sidebar-width] flex-col bg-sidebar text-sidebar-foreground",
						className
					)}
					ref={ref}
					{...props}
				>
					{children}
				</div>
			);
		}

		if (isMobile) {
			return (
				<Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
					<SheetContent
						data-sidebar="sidebar"
						data-mobile="true"
						className="w-[--sidebar-width] bg-primary shadow-lg p-0 text-sidebar-foreground [&>button]:hidden p-2"
						style={
							{
								"--sidebar-width": SIDEBAR_WIDTH_MOBILE,
							} as CSSProperties
						}
						side={side}
					>
						<div
							className={cn(
								"flex h-full w-full flex-col",
								published ? "-mt-6" : "pt-8"
							)}
						>
							{children}
						</div>
					</SheetContent>
				</Sheet>
			);
		}

		const selectedValueIndex = value === undefined ? -1 : values.indexOf(value);
		const thisValueIndex = thisValue === undefined ? -1 : values.indexOf(thisValue);
		let transitionVariant = "center";
		if (selectedValueIndex !== thisValueIndex) {
			transitionVariant = selectedValueIndex > thisValueIndex ? "left" : "right";
		} else if (values.length === 0) {
			transitionVariant = "center";
		}

		return (
			<motion.div
				variants={{
					left: { x: "-100%", opacity: 0 },
					center: { x: 0, opacity: 1 },
					right: { x: "100%", opacity: 0 },
				}}
				animate={transitionVariant}
				transition={{ duration: 0.1, bounceDamping: 0, bounceStiffness: 0 }}
				ref={ref}
				className={cn(
					{ "w-0": transitionVariant === "left" },
					{ "w-0": transitionVariant === "right" },
					"-z-0 group peer hidden md:block text-sidebar-foreground overflow-x-hidden"
				)}
				data-state={state}
				data-collapsible={state === "collapsed" ? collapsible : ""}
				data-variant={variant}
				data-side={side}
			>
				<div
					className={cn(
						"duration-200 z-surface fixed hidden h-full w-[--sidebar-width] transition-[left,right,width] ease-linear md:flex",
						{
							"inset-y-0": insetY,
							"left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]":
								insetX && side === "left",
							"right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]":
								insetX && side === "right",
						},
						// Adjust the padding for floating and inset variants.
						variant === "floating" || variant === "inset"
							? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
							: "group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l",
						className
					)}
					{...props}
				>
					<div
						data-sidebar="sidebar"
						className="flex gap-px h-full w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
					>
						{children}
					</div>
				</div>
				{/* This is what handles the sidebar gap on desktop */}
				<div
					className={cn(
						"duration-200 relative h-full w-[--sidebar-width] bg-transparent transition-[width] ease-linear",
						"group-data-[collapsible=offcanvas]:w-0",
						"group-data-[side=right]:rotate-180",
						variant === "floating" || variant === "inset"
							? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
							: "group-data-[collapsible=icon]:w-[--sidebar-width-icon]"
					)}
				/>
			</motion.div>
		);
	}
);
Sidebar.displayName = "Sidebar";

const SidebarTrigger = forwardRef<
	ElementRef<typeof IconButton>,
	Omit<IconButtonProps, "icon" | "label">
>(({ className, onClick, ...props }, ref) => {
	const { toggleSidebar } = useSidebar();

	return (
		<IconButton
			ref={ref}
			data-sidebar="trigger"
			preset="quaternary"
			className={cn(className)}
			onClick={(event) => {
				onClick?.(event);
				toggleSidebar();
			}}
			{...props}
			icon={<SidePanelOpenIcon />}
			label="Toggle Sidebar"
		/>
	);
});
SidebarTrigger.displayName = "SidebarTrigger";

const SidebarRail = forwardRef<HTMLButtonElement, ComponentProps<"button">>(
	({ className, ...props }, ref) => {
		const { toggleSidebar } = useSidebar();

		return (
			<button
				ref={ref}
				data-sidebar="rail"
				aria-label="Toggle Sidebar"
				tabIndex={-1}
				onClick={toggleSidebar}
				title="Toggle Sidebar"
				className={cn(
					"absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
					"[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
					"[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
					"group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-sidebar",
					"[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
					"[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
					className
				)}
				{...props}
			/>
		);
	}
);
SidebarRail.displayName = "SidebarRail";

const SidebarInset = forwardRef<HTMLDivElement, ComponentProps<"main">>(
	({ className, ...props }, ref) => {
		return (
			<main
				ref={ref}
				className={cn(
					"relative flex min-h-svh flex-1 flex-col bg-background",
					"peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
					className
				)}
				{...props}
			/>
		);
	}
);
SidebarInset.displayName = "SidebarInset";

const SidebarInput = forwardRef<ElementRef<typeof Input>, ComponentProps<typeof Input>>(
	({ className, ...props }, ref) => {
		return (
			<Input
				ref={ref}
				data-sidebar="input"
				className={cn("h-8", "w-full", "bg-background", "shadow-none", className)}
				{...props}
			/>
		);
	}
);
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				data-sidebar="header"
				className={cn("flex flex-col gap-2 p-2", className)}
				{...props}
			/>
		);
	}
);
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				data-sidebar="footer"
				className={cn("flex flex-col gap-2 p-2", className)}
				{...props}
			/>
		);
	}
);
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = forwardRef<ElementRef<typeof Separator>, ComponentProps<typeof Separator>>(
	({ className, ...props }, ref) => {
		return (
			<Separator
				ref={ref}
				data-sidebar="separator"
				className={cn("w-auto border-t", className)}
				{...props}
			/>
		);
	}
);
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				data-sidebar="content"
				className={cn(
					"flex min-h-0 flex-1 flex-col gap-4 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
					className
				)}
				{...props}
			/>
		);
	}
);
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => {
		return (
			<div
				ref={ref}
				data-sidebar="group"
				className={cn("relative flex w-full min-w-0 flex-col px-2", className)}
				{...props}
			/>
		);
	}
);
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = forwardRef<HTMLDivElement, ComponentProps<"div"> & { asChild?: boolean }>(
	({ className, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : "div";

		return (
			<Comp
				ref={ref}
				data-sidebar="group-label"
				className={cn(
					"duration-200",
					"flex",
					"h-8",
					"shrink-0",
					"items-center",
					"rounded-md",
					"px-2",
					"text-xs",
					"font-medium",
					"text-sidebar-foreground/70",
					"outline-none",
					"transition-[margin,opa]",
					"ease-linear",
					"[&>svg]:size-4",
					"[&>svg]:shrink-0",
					"group-data-[collapsible=icon]:-mt-8",
					"group-data-[collapsible=icon]:opacity-0",
					className
				)}
				{...props}
			/>
		);
	}
);
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupAction = forwardRef<
	HTMLButtonElement,
	ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			ref={ref}
			data-sidebar="group-action"
			className={cn(
				"absolute",
				"right-3",
				"top-3.5",
				"flex",
				"aspect-square",
				"w-5",
				"items-center",
				"justify-center",
				"rounded-md",
				"p-0",
				"text-sidebar-foreground",
				"outline-none",
				"transition-transform",
				"hover:bg-sidebar-accent",
				"hover:text-sidebar-accent-foreground",
				"[&>svg]:size-4",
				"[&>svg]:shrink-0",
				// Increases the hit area of the button on mobile.
				"after:absolute after:-inset-2 after:md:hidden",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			{...props}
		/>
	);
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			data-sidebar="group-content"
			className={cn("w-full text-sm", className)}
			{...props}
		/>
	)
);
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = forwardRef<HTMLUListElement, ComponentProps<"ul">>(
	({ className, ...props }, ref) => (
		<ul
			ref={ref}
			data-sidebar="menu"
			className={cn("flex w-full min-w-0 flex-col gap-px", className)}
			{...props}
		/>
	)
);
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = forwardRef<HTMLLIElement, ComponentProps<"li">>(
	({ className, ...props }, ref) => (
		<li
			ref={ref}
			data-sidebar="menu-item"
			className={cn("group/menu-item relative", className)}
			{...props}
		/>
	)
);
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
	[
		"peer/menu-button",
		"flex",
		"w-full",
		"items-center",
		"gap-2",
		"overflow-hidden",
		"rounded-md",
		"px-2",
		"py-1",

		"data-[active=true]:font-medium",
		"text-left",
		"text-sm",
		"text-primary",
		"no-underline",
		"hover:underline",
		"hover:text-primary-hover",
		"focus:text-primary-focus",
		"active:text-primary-active",
		"data-[state=open]:hover:text-primary-hover",
		"data-[state=open]:hover:text-primary-hover",
		"data-[active=true]:focus:text-primary-active",
		"data-[active=true]:text-primary-active",

		"[&>svg]:fill-icon-default",
		"[&>svg]:hover:fill-icon-hover",
		"[&>svg]:focus:fill-icon-focus",
		"[&>svg]:active:fill-icon-active",
		"[&>svg]:data-[state=open]:hover:fill-icon-hover",
		"[&>svg]:data-[state=open]:hover:fill-icon-hover",
		"[&>svg]:data-[active=true]:focus:fill-icon-active",

		"hover:bg-button-normal-secondary-hover",
		"active:bg-sidebar-accent",
		"data-[state=open]:hover:bg-sidebar-accent",
		"data-[active=true]:bg-sidebar-accent",

		"outline-none",
		"outline-0",
		"outline-offset-0",
		"focus:outline-2",
		"focus:outline-brand",
		"ring-0",
		"active:ring-0",

		"transition-[width,height,padding]",

		"disabled:pointer-events-none",
		"disabled:opacity-50",
		"group-has-[[data-sidebar=menu-action]]/menu-item:pr-8",
		"aria-disabled:pointer-events-none",
		"aria-disabled:opacity-50",

		"group-data-[collapsible=icon]:!size-8",
		"group-data-[collapsible=icon]:!p-2",
		"[&>span:last-child]:break-words",
		"[&>svg]:size-4",
		"[&>svg]:shrink-0",
	].join(" "),
	{
		variants: {
			variant: {
				default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
				outline:
					"bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
			},
			size: {
				default: "min-h-8 text-sm",
				sm: "min-h-7 text-xs",
				lg: "min-h-12 text-sm group-data-[collapsible=icon]:!p-0",
			},
			theme: {
				light: "data-[active=true]:bg-palette-gray-4",
				dark: "data-[active=true]:bg-palette-gray-4",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
			theme: "light",
		},
	}
);

const SidebarMenuButton = forwardRef<
	HTMLButtonElement,
	ComponentProps<"button"> & {
		asChild?: boolean;
		isActive?: boolean;
		tooltip?: string | ComponentProps<typeof MomentTooltip.Content>;
	} & VariantProps<typeof sidebarMenuButtonVariants>
>(
	(
		{
			asChild = false,
			isActive = false,
			variant = "default",
			size = "default",
			tooltip,
			className,
			...props
		},
		ref
	) => {
		const Comp = asChild ? Slot : "button";
		const { isMobile, state } = useSidebar();

		const { theme: _theme } = useTheme();
		let theme: "light" | "dark";
		if (_theme === "dark") {
			theme = "dark";
		} else {
			theme = "light";
		}

		const button = (
			<Comp
				ref={ref}
				data-sidebar="menu-button"
				data-size={size}
				data-active={isActive}
				className={cn(
					"group",
					sidebarMenuButtonVariants({ variant, size, theme }),
					className
				)}
				{...props}
			/>
		);

		if (!tooltip) {
			return button;
		}

		if (typeof tooltip === "string") {
			tooltip = {
				children: tooltip,
			};
		}

		return (
			<MomentTooltip.Provider>
				<MomentTooltip.Root>
					<MomentTooltip.Trigger asChild>{button}</MomentTooltip.Trigger>
					<MomentTooltip.Content
						side="right"
						align="center"
						hidden={state !== "collapsed" || isMobile}
						{...tooltip}
					/>
				</MomentTooltip.Root>
			</MomentTooltip.Provider>
		);
	}
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = forwardRef<
	HTMLButtonElement,
	ComponentProps<"button"> & {
		asChild?: boolean;
		showOnHover?: boolean;
	}
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			ref={ref}
			data-sidebar="menu-action"
			className={cn(
				"absolute",
				"right-1",
				"top-1.5",
				"flex",
				"aspect-square",
				"w-5",
				"items-center",
				"justify-center",
				"rounded-md",
				"p-0",
				"text-primary",
				"outline-none",
				"transition-transform",
				"hover:bg-button-normal-secondary-hover",
				"hover:text-primary-hover",
				"peer-hover/menu-button:text-sidebar-accent-foreground",
				"[&>svg]:size-4",
				"[&>svg]:text-tertiary",
				"[&>svg]:shrink-0",
				// Increases the hit area of the button on mobile.
				"after:absolute after:-inset-2 after:md:hidden",
				"peer-data-[size=sm]/menu-button:top-1",
				"peer-data-[size=default]/menu-button:top-1.5",
				"peer-data-[size=lg]/menu-button:top-2.5",
				"group-data-[collapsible=icon]:hidden",
				showOnHover &&
					"group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0",
				className
			)}
			{...props}
		/>
	);
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = forwardRef<HTMLDivElement, ComponentProps<"div">>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			data-sidebar="menu-badge"
			className={cn(
				"absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none",
				"peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
				"peer-data-[size=sm]/menu-button:top-1",
				"peer-data-[size=default]/menu-button:top-1.5",
				"peer-data-[size=lg]/menu-button:top-2.5",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			{...props}
		/>
	)
);
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = forwardRef<
	HTMLDivElement,
	ComponentProps<"div"> & {
		showIcon?: boolean;
	}
>(({ className, showIcon = false, ...props }, ref) => {
	// Random width between 50 to 90%.
	const width = useMemo(() => {
		return `${Math.floor(Math.random() * 40) + 50}%`;
	}, []);

	return (
		<div
			ref={ref}
			data-sidebar="menu-skeleton"
			className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
			{...props}
		>
			{showIcon && (
				<Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />
			)}
			<Skeleton
				className="h-4 flex-1 max-w-[--skeleton-width]"
				data-sidebar="menu-skeleton-text"
				style={
					{
						"--skeleton-width": width,
					} as CSSProperties
				}
			/>
		</div>
	);
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = forwardRef<HTMLUListElement, ComponentProps<"ul">>(
	({ className, ...props }, ref) => (
		<ul
			ref={ref}
			data-sidebar="menu-sub"
			className={cn(
				// mr-[1px] here is because shadbar very annoyingly is setting the width of the
				// sidebar to 1px more than it should be, and to fix it I'll really have to look at
				// why this is.
				"ml-3.5 mr-[1px] flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border pl-2.5 py-0.5",
				"group-data-[collapsible=icon]:hidden",
				className
			)}
			{...props}
		/>
	)
);
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = forwardRef<HTMLLIElement, ComponentProps<"li">>(({ ...props }, ref) => (
	<li ref={ref} {...props} />
));
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = forwardRef<
	HTMLAnchorElement,
	ComponentProps<"a"> & {
		asChild?: boolean;
		isActive?: boolean;
	} & VariantProps<typeof sidebarMenuButtonVariants>
>(({ asChild = false, size = "default", isActive, className, ...props }, ref) => {
	const Comp = asChild ? Slot : "a";

	const { theme: _theme } = useTheme();
	let theme: "light" | "dark";
	if (_theme === "dark") {
		theme = "dark";
	} else {
		theme = "light";
	}

	return (
		<Comp
			ref={ref}
			data-sidebar="menu-sub-button"
			data-size={size}
			data-active={isActive}
			className={cn(
				className,
				sidebarMenuButtonVariants({ variant: "default", size, theme })
			)}
			{...props}
		/>
	);
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInput,
	SidebarInset,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarSeparator,
	SidebarTrigger,
	useSidebar,
};
